import React from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const ConfirmAppointmentModal = ({
  isOpen,
  onClose,
  onConfirm,
  warningMessage,
  appointmentDetails = {}
}) => {
  if (!isOpen) return null;

  const { date, time, clinicianName, clientName } = appointmentDetails;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-70 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 transition-opacity" />

        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div data-component="ConfirmAppointmentModal" className="inline-block w-full max-w-md my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-sm">
          <div className="absolute right-4 top-4">
            <button
              type="button"
              className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              onClick={onClose}>
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="bg-white px-6 py-6">
            {/* Warning Icon and Title */}
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
              </div>
              <Dialog.Title
                as="h3"
                className="ml-3 text-lg font-medium leading-6 text-gray-900">
                Confirm Appointment Creation
              </Dialog.Title>
            </div>

            {/* Warning Message */}
            <div className="mb-6">
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-orange-800 font-medium">
                      Scheduling Outside Normal Hours
                    </p>
                    <p className="mt-1 text-sm text-orange-700">
                      {warningMessage}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Appointment Details */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Appointment Details:</h4>
              <div className="bg-gray-50 p-3 rounded-sm space-y-2 text-sm">
                {clientName && (
                  <div>
                    <span className="font-medium text-gray-600">Client:</span>
                    <span className="ml-2 text-gray-900">{clientName}</span>
                  </div>
                )}
                {clinicianName && (
                  <div>
                    <span className="font-medium text-gray-600">Clinician:</span>
                    <span className="ml-2 text-gray-900">{clinicianName}</span>
                  </div>
                )}
                {date && (
                  <div>
                    <span className="font-medium text-gray-600">Date:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {time && (
                  <div>
                    <span className="font-medium text-gray-600">Time:</span>
                    <span className="ml-2 text-gray-900">{time}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to create this appointment outside of the normal 
                scheduling parameters?
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-x-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-sm bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="rounded-sm bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600">
                Yes, Create Anyway
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmAppointmentModal;